"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DATABASE_CELL_CONVERSIONS = void 0;
exports.databaseTextOptionNames = databaseTextOptionNames;
exports.databaseTypeChangeReadsTableValues = databaseTypeChangeReadsTableValues;
exports.convertDatabaseCellValue = convertDatabaseCellValue;
const database_cell_text_parse_1 = require("./database-cell-text-parse");
function options(data) {
    if (!Array.isArray(data.options))
        return [];
    return data.options.filter((option) => !!option &&
        typeof option === 'object' &&
        typeof option.id === 'string' &&
        typeof option.value === 'string');
}
const text = (value) => (typeof value === 'string' ? value : null);
const finite = (value) => typeof value === 'number' && Number.isFinite(value) ? value : null;
const clampProgress = (value) => Math.min(100, Math.max(0, value));
function optionIdsByName(data, names) {
    const byName = new Map(options(data).map(option => [option.value, option.id]));
    const ids = [];
    for (const name of names) {
        const id = byName.get(name);
        if (id !== undefined && !ids.includes(id))
            ids.push(id);
    }
    return ids;
}
function optionNames(data, ids) {
    const byId = new Map(options(data).map(option => [option.id, option.value]));
    return ids.map(id => (typeof id === 'string' ? (byId.get(id) ?? '') : ''));
}
const textParts = (value) => value
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
/**
 * The option names a text cell stands for when its column changes to
 * `toType`: the first comma-separated part for a select, every distinct part
 * for a multi-select, none for any other type. The type change creates one
 * option per distinct name of the table, and every reader resolves a stored
 * text against those options by the same names.
 */
function databaseTextOptionNames(toType, value) {
    const parts = textParts(value);
    if (toType === 'select')
        return parts.slice(0, 1);
    if (toType === 'multi-select')
        return [...new Set(parts)];
    return [];
}
/**
 * One converter per supported type pair, from a stored value to the stored
 * value of the target type, cell by cell. The only rule set: readers of a
 * type-changed column and the editor's column-wide conversion both use it.
 */
const CONVERTERS = {
    // Text to options resolves names against the options the target column
    // defines; the type change creates them from every table part.
    'rich-text>select': ({ value, toData }) => optionIdsByName(toData, databaseTextOptionNames('select', text(value) ?? ''))[0] ?? null,
    'rich-text>multi-select': ({ value, toData }) => {
        const ids = optionIdsByName(toData, databaseTextOptionNames('multi-select', text(value) ?? ''));
        return ids.length ? ids : null;
    },
    'rich-text>number': ({ value }) => {
        const raw = text(value);
        return raw ? (0, database_cell_text_parse_1.parseDatabaseNumberText)(raw) : null;
    },
    'rich-text>date': ({ value }) => {
        const raw = text(value);
        return raw
            ? (0, database_cell_text_parse_1.parseDatabaseDateText)(raw, database_cell_text_parse_1.DATABASE_STORED_TEXT_TIME_ZONE)
            : null;
    },
    'rich-text>progress': ({ value }) => {
        const progress = parseInt(text(value) ?? '', 10);
        return Number.isNaN(progress) ? null : clampProgress(progress);
    },
    'rich-text>checkbox': ({ value }) => {
        const raw = text(value)?.toLowerCase();
        return raw === 'yes' || raw === 'true' ? true : null;
    },
    'checkbox>rich-text': ({ value }) => (value === true ? 'Yes' : 'No'),
    'multi-select>rich-text': ({ value, fromData }) => Array.isArray(value) ? optionNames(fromData, value).join(',') : null,
    'select>rich-text': ({ value, fromData }) => typeof value === 'string' && value
        ? optionNames(fromData, [value])[0]
        : null,
    'number>rich-text': ({ value }) => {
        const number = finite(value);
        return number === null ? null : String(number);
    },
    'progress>rich-text': ({ value }) => {
        const number = finite(value);
        return number === null ? null : String(number);
    },
    'multi-select>select': ({ value }) => Array.isArray(value) && typeof value[0] === 'string' ? value[0] : null,
    'select>multi-select': ({ value }) => typeof value === 'string' && value ? [value] : null,
    'number>progress': ({ value }) => clampProgress(finite(value) ?? 0),
    'progress>number': ({ value }) => finite(value),
};
/**
 * Type changes whose target column data comes from the stored values: text to
 * options creates one option per distinct name. The editor reads the values of
 * every table part for them, not only the loaded rows.
 */
const TYPE_CHANGES_READING_TABLE_VALUES = new Set([
    'rich-text>select',
    'rich-text>multi-select',
]);
/** Whether changing `fromType` to `toType` reads the values of the whole table. */
function databaseTypeChangeReadsTableValues(fromType, toType) {
    return TYPE_CHANGES_READING_TABLE_VALUES.has(`${fromType}>${toType}`);
}
/** Every type change with a cell rule, as `{ fromType, toType }`. */
exports.DATABASE_CELL_CONVERSIONS = Object.keys(CONVERTERS).map(pair => {
    const [fromType, toType] = pair.split('>');
    return { fromType, toType };
});
/**
 * Reads a stored value of one column type as a value of another; null when
 * the value does not fit the target type. Pure and the same on every device
 * and on the server, so every reader shows the same value.
 */
function convertDatabaseCellValue(input) {
    if (input.value === null || input.value === undefined)
        return null;
    if (input.fromType === input.toType)
        return input.value;
    const converter = CONVERTERS[`${input.fromType}>${input.toType}`];
    return converter ? (converter(input) ?? null) : null;
}
